/**
 * FulfillmentOrderDetail — Type 2 full-bleed detail for a single fulfillment order.
 * Route: /fulfillment/orders/:id
 *
 * Layout copies PODetail.tsx (Day 50): outer flex-col h-full -m-4, fixed
 * toolbar with back nav + status pill + Edit/Cancel buttons, scrollable
 * two-column content (flex-1 left + sticky w-72 right).
 *
 * Hosts the Reported-vs-Received gap card (Goflow #28) when
 * units_reported_received differs from quantity. The card is the visible
 * surface for FBA/WFS reporting drift until real integration lands.
 *
 * Status transitions: single-row equivalents of the formerly-inline
 * DetailPanel from FulfillmentQueue.tsx. Bulk transitions live on the queue.
 */

import { useState, useEffect, useCallback, type JSX } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertCircle, AlertTriangle, Copy, Check } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import ArbiSelect from '../../components/ui/ArbiSelect';
import { STATUS_COLORS, STATUS_LABELS, CHANNEL_COLORS, CHANNEL_LABELS } from './FulfillmentQueue';
import { Toast, type ToastState } from '../../components/ui/Toast';

interface OrderDetail {
  id: string;
  order_number: string | null;
  channel: string | null;
  marketplace_order_id: string | null;
  status: string;
  previous_status: string | null;
  quantity: number;
  units_reported_received: number | null;
  unit_price: number | null;
  tracking_number: string | null;
  carrier: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  on_hold_reason: string | null;
  followup_notes: string | null;
  discrepancy_notes: string | null;
  created_at: string;
  updated_at: string;
  product_id: string | null;
  product_isg_sku: string | null;
  product_title: string | null;
  product_brand: string | null;
  distributor_id: string | null;
  distributor_name: string | null;
}

interface RawJoin {
  id: string;
  order_number: string | null;
  channel: string | null;
  marketplace_order_id: string | null;
  status: string;
  previous_status: string | null;
  quantity: number;
  units_reported_received: number | null;
  unit_price: number | null;
  tracking_number: string | null;
  carrier: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  on_hold_reason: string | null;
  followup_notes: string | null;
  discrepancy_notes: string | null;
  created_at: string;
  updated_at: string;
  product_id: string | null;
  products: { isg_sku: string | null; title: string | null; brand: string | null } | null;
  distributor_id: string | null;
  distributors: { name: string | null } | null;
}

const CARRIER_OPTIONS: { value: string; label: string }[] = [
  { value: 'USPS', label: 'USPS' },
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Amazon', label: 'Amazon' },
  { value: 'Other', label: 'Other' },
];

function fmtCurrency(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  return `$${Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function FulfillmentOrderDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer extends the existing dist_rep gate at L318
  // (`if (isDistRep) return null;` in renderPrimaryTransition). Single
  // edit covers all 10+ status-transition buttons (Mark Processing, Move
  // to Review, Verify, Start Fulfilling, Start Picking, Mark Packed, Mark
  // Awaiting Label, Mark Awaiting Pickup, Mark Shipped, Mark Delivered).
  const { isDistRep, isViewer } = useApp();

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editTracking, setEditTracking] = useState('');
  const [editCarrier, setEditCarrier] = useState('');
  const [editStatusNotes, setEditStatusNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    const { data, error } = await supabase
      .from('fulfillment_orders')
      .select(
        `
        id, order_number, channel, marketplace_order_id, status, previous_status,
        quantity, units_reported_received, unit_price,
        tracking_number, carrier, shipped_at, delivered_at,
        on_hold_reason, followup_notes, discrepancy_notes,
        created_at, updated_at,
        product_id, products(isg_sku, title, brand),
        distributor_id, distributors(name)
        `
      )
      .eq('id', id)
      .maybeSingle();

    if (error) {
      setLoadError(error.message);
      setLoading(false);
      return;
    }
    if (!data) {
      setLoadError('Order not found');
      setLoading(false);
      return;
    }
    const r = data as unknown as RawJoin;
    const detail: OrderDetail = {
      id: r.id,
      order_number: r.order_number,
      channel: r.channel,
      marketplace_order_id: r.marketplace_order_id,
      status: r.status,
      previous_status: r.previous_status,
      quantity: r.quantity,
      units_reported_received: r.units_reported_received,
      unit_price: r.unit_price,
      tracking_number: r.tracking_number,
      carrier: r.carrier,
      shipped_at: r.shipped_at,
      delivered_at: r.delivered_at,
      on_hold_reason: r.on_hold_reason,
      followup_notes: r.followup_notes,
      discrepancy_notes: r.discrepancy_notes,
      created_at: r.created_at,
      updated_at: r.updated_at,
      product_id: r.product_id,
      product_isg_sku: r.products?.isg_sku ?? null,
      product_title: r.products?.title ?? null,
      product_brand: r.products?.brand ?? null,
      distributor_id: r.distributor_id,
      distributor_name: r.distributors?.name ?? null,
    };
    setOrder(detail);
    setEditTracking(detail.tracking_number ?? '');
    setEditCarrier(detail.carrier ?? '');
    setEditStatusNotes(
      detail.status === 'on_hold'
        ? (detail.on_hold_reason ?? '')
        : detail.status === 'followup'
          ? (detail.followup_notes ?? '')
          : detail.status === 'discrepancy'
            ? (detail.discrepancy_notes ?? '')
            : ''
    );
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const copyField = (text: string, field: string): void => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const transitionStatus = async (
    nextStatus: string,
    extra: Record<string, unknown> = {}
  ): Promise<void> => {
    if (!order) return;
    setSaving(true);
    const update: Record<string, unknown> = {
      status: nextStatus,
      previous_status: order.status,
      updated_at: new Date().toISOString(),
      ...extra,
    };
    const { error } = await supabase
      .from('fulfillment_orders')
      .update(update as never)
      .eq('id', order.id);
    setSaving(false);
    if (error) {
      setToast({ kind: 'error', message: `Status change failed: ${error.message}` });
      return;
    }
    setToast({ kind: 'success', message: `Marked ${STATUS_LABELS[nextStatus] || nextStatus}` });
    void load();
  };

  const saveEdits = async (): Promise<void> => {
    if (!order) return;
    setSaving(true);
    const update: Record<string, unknown> = {
      tracking_number: editTracking.trim() || null,
      carrier: editCarrier.trim() || null,
      updated_at: new Date().toISOString(),
    };
    if (order.status === 'on_hold') update.on_hold_reason = editStatusNotes.trim() || null;
    if (order.status === 'followup') update.followup_notes = editStatusNotes.trim() || null;
    if (order.status === 'discrepancy') update.discrepancy_notes = editStatusNotes.trim() || null;
    const { error } = await supabase
      .from('fulfillment_orders')
      .update(update as never)
      .eq('id', order.id);
    setSaving(false);
    if (error) {
      setToast({ kind: 'error', message: `Save failed: ${error.message}` });
      return;
    }
    setEditMode(false);
    setToast({ kind: 'success', message: 'Order updated' });
    void load();
  };

  const cancelOrder = async (): Promise<void> => {
    if (!order) return;
    await transitionStatus('cancelled');
    setConfirmCancel(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-sm text-ink-muted">Loading order…</span>
      </div>
    );
  }

  if (loadError || !order) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
          <button
            onClick={() => navigate('/fulfillment/orders')}
            className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink-primary transition-colors mb-1 cursor-pointer"
          >
            <ChevronLeft size={12} /> Back to Orders
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle size={32} className="text-danger mx-auto mb-2" />
            <p className="text-sm text-ink-primary">{loadError || 'Order not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const s = order.status;
  const channelLabel = (order.channel && CHANNEL_LABELS[order.channel]) || order.channel || '—';
  const channelClass =
    (order.channel && CHANNEL_COLORS[order.channel]) || 'bg-surface-3 text-ink-muted';
  const statusClass = STATUS_COLORS[s] || 'bg-surface-3 text-ink-muted';
  const statusLabel = STATUS_LABELS[s] || s;

  const reportedGap =
    order.units_reported_received !== null && order.units_reported_received !== order.quantity;
  const gapDelta =
    order.units_reported_received !== null ? order.units_reported_received - order.quantity : null;

  const showStatusNotes =
    (s === 'on_hold' && (order.on_hold_reason || editMode)) ||
    (s === 'followup' && (order.followup_notes || editMode)) ||
    (s === 'discrepancy' && (order.discrepancy_notes || editMode));
  const statusNotesLabel =
    s === 'on_hold' ? 'Hold Reason' : s === 'followup' ? 'Followup Notes' : 'Discrepancy Notes';

  // Single-row primary transition buttons (mirrors retired DetailPanel logic)
  const renderPrimaryTransition = (): JSX.Element | null => {
    if (isDistRep || isViewer) return null;
    switch (s) {
      case 'new_order':
        return (
          <button
            onClick={() => void transitionStatus('processing')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Mark Processing
          </button>
        );
      case 'processing':
        return (
          <button
            onClick={() => void transitionStatus('review')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Move to Review
          </button>
        );
      case 'review':
        return (
          <button
            onClick={() => void transitionStatus('verified')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Verify
          </button>
        );
      case 'verified':
        return (
          <button
            onClick={() => void transitionStatus('fulfilling')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Start Fulfilling
          </button>
        );
      case 'fulfilling':
        return (
          <button
            onClick={() => void transitionStatus('picking')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Start Picking
          </button>
        );
      case 'picking':
        return (
          <button
            onClick={() => void transitionStatus('packed')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Mark Packed
          </button>
        );
      case 'packed':
        return (
          <button
            onClick={() => void transitionStatus('awaiting_label')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Mark Awaiting Label
          </button>
        );
      case 'awaiting_label':
        return (
          <button
            onClick={() => void transitionStatus('awaiting_pickup')}
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Mark Awaiting Pickup
          </button>
        );
      case 'awaiting_pickup':
        return (
          <button
            onClick={() =>
              void transitionStatus('shipped', { shipped_at: new Date().toISOString() })
            }
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Mark Shipped
          </button>
        );
      case 'shipped':
        return (
          <button
            onClick={() =>
              void transitionStatus('delivered', { delivered_at: new Date().toISOString() })
            }
            disabled={saving}
            className="arbi-btn-primary text-xs w-full"
          >
            Mark Delivered
          </button>
        );
      case 'on_hold':
        if (order.previous_status) {
          return (
            <button
              onClick={() => void transitionStatus(order.previous_status as string)}
              disabled={saving}
              className="arbi-btn-primary text-xs w-full"
            >
              Release Hold → {STATUS_LABELS[order.previous_status] || order.previous_status}
            </button>
          );
        }
        return null;
      default:
        return null;
    }
  };

  const editable = !['delivered', 'channel_fulfilled', 'cancelled'].includes(s);
  const cancellable = !['delivered', 'channel_fulfilled', 'cancelled'].includes(s);

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel order?"
        message={`Cancel order ${order.order_number}? This sets status to Cancelled and cannot be undone.`}
        confirmLabel="Cancel Order"
        onConfirm={cancelOrder}
        onCancel={() => setConfirmCancel(false)}
      />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <button
          onClick={() => navigate('/fulfillment/orders')}
          className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink-primary transition-colors mb-1 cursor-pointer"
        >
          <ChevronLeft size={12} /> Back to Orders
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">
              Order <span className="font-mono">{order.order_number}</span>
            </h1>
            <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', statusClass)}>
              {statusLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {editable && !editMode && (
              <button onClick={() => setEditMode(true)} className="arbi-btn-ghost text-xs">
                Edit
              </button>
            )}
            {editMode && (
              <>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditTracking(order.tracking_number ?? '');
                    setEditCarrier(order.carrier ?? '');
                    setEditStatusNotes(
                      s === 'on_hold'
                        ? (order.on_hold_reason ?? '')
                        : s === 'followup'
                          ? (order.followup_notes ?? '')
                          : s === 'discrepancy'
                            ? (order.discrepancy_notes ?? '')
                            : ''
                    );
                  }}
                  className="arbi-btn-ghost text-xs"
                  disabled={saving}
                >
                  Discard
                </button>
                <button
                  onClick={() => void saveEdits()}
                  disabled={saving}
                  className="arbi-btn-primary text-xs"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
            {cancellable && !editMode && !isDistRep && (
              <button
                onClick={() => setConfirmCancel(true)}
                className="arbi-btn-ghost text-xs text-danger"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex gap-4">
          {/* Left column */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Order Info */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">Order Info</h2>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                <div>
                  <span className="text-ink-muted">Order #</span>
                  <p className="font-mono text-ink-bright flex items-center gap-1">
                    {order.order_number}
                    {order.order_number && (
                      <button
                        onClick={() => copyField(order.order_number!, 'orderNum')}
                        className="p-0.5 cursor-pointer"
                        aria-label="Copy order number"
                      >
                        {copiedField === 'orderNum' ? (
                          <Check size={10} className="text-success" />
                        ) : (
                          <Copy size={10} className="text-ink-muted" />
                        )}
                      </button>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-ink-muted">Channel</span>
                  <p>
                    <span
                      className={clsx(
                        'inline-block px-1.5 py-0.5 rounded text-2xs font-medium',
                        channelClass
                      )}
                    >
                      {channelLabel}
                    </span>
                  </p>
                </div>
                {order.marketplace_order_id && (
                  <div>
                    <span className="text-ink-muted">Marketplace Order ID</span>
                    <p className="font-mono text-ink-primary text-2xs flex items-center gap-1">
                      {order.marketplace_order_id}
                      <button
                        onClick={() => copyField(order.marketplace_order_id!, 'mpoid')}
                        className="p-0.5 cursor-pointer"
                        aria-label="Copy marketplace order ID"
                      >
                        {copiedField === 'mpoid' ? (
                          <Check size={10} className="text-success" />
                        ) : (
                          <Copy size={10} className="text-ink-muted" />
                        )}
                      </button>
                    </p>
                  </div>
                )}
                {order.distributor_name && (
                  <div>
                    <span className="text-ink-muted">Distributor</span>
                    <p className="text-ink-primary">{order.distributor_name}</p>
                  </div>
                )}
                <div>
                  <span className="text-ink-muted">Product</span>
                  <p>
                    {order.product_isg_sku ? (
                      <button
                        onClick={() => navigate(`/catalog/products/${order.product_isg_sku}`)}
                        className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
                      >
                        {order.product_isg_sku}
                      </button>
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                    {order.product_title && (
                      <span className="text-ink-secondary block truncate">
                        {order.product_title}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-ink-muted">Quantity</span>
                  <p className="font-mono text-ink-bright">{order.quantity}</p>
                </div>
                {!isDistRep && (
                  <div>
                    <span className="text-ink-muted">Unit Price</span>
                    <p className="font-mono text-ink-primary">{fmtCurrency(order.unit_price)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Status Timeline */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">Status Timeline</h2>
              {/* TODO: full status history requires fulfillment_order_status_log table — Phase 4+ */}
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center gap-3">
                  <span className="font-mono text-ink-muted whitespace-nowrap">
                    {fmtDateTime(order.created_at)}
                  </span>
                  <span className="text-ink-primary">Created · new_order</span>
                </li>
                {order.previous_status && (
                  <li className="flex items-center gap-3">
                    <span className="font-mono text-ink-muted whitespace-nowrap">
                      {fmtDateTime(order.updated_at)}
                    </span>
                    <span className="text-ink-primary">
                      {STATUS_LABELS[order.previous_status] || order.previous_status} →{' '}
                      {statusLabel}
                    </span>
                  </li>
                )}
                {order.shipped_at && (
                  <li className="flex items-center gap-3">
                    <span className="font-mono text-ink-muted whitespace-nowrap">
                      {fmtDateTime(order.shipped_at)}
                    </span>
                    <span className="text-ink-primary">Shipped</span>
                  </li>
                )}
                {order.delivered_at && (
                  <li className="flex items-center gap-3">
                    <span className="font-mono text-ink-muted whitespace-nowrap">
                      {fmtDateTime(order.delivered_at)}
                    </span>
                    <span className="text-ink-primary">Delivered</span>
                  </li>
                )}
              </ul>
            </div>

            {/* Tracking & Shipment */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">
                Tracking &amp; Shipment
              </h2>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                <div>
                  <span className="text-ink-muted">Tracking #</span>
                  {editMode ? (
                    <input
                      value={editTracking}
                      onChange={(e) => setEditTracking(e.target.value)}
                      placeholder="Tracking number"
                      className="arbi-input mt-1 w-full text-xs font-mono"
                    />
                  ) : (
                    <p className="font-mono text-ink-primary">{order.tracking_number || '—'}</p>
                  )}
                </div>
                <div>
                  <span className="text-ink-muted">Carrier</span>
                  {editMode ? (
                    <div className="mt-1">
                      <ArbiSelect
                        value={editCarrier}
                        onChange={(v) => setEditCarrier(v)}
                        options={CARRIER_OPTIONS}
                        placeholder="Select carrier…"
                      />
                    </div>
                  ) : (
                    <p className="text-ink-primary">{order.carrier || '—'}</p>
                  )}
                </div>
                <div>
                  <span className="text-ink-muted">Shipped At</span>
                  <p className="font-mono text-ink-secondary">{fmtDateTime(order.shipped_at)}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Delivered At</span>
                  <p className="font-mono text-ink-secondary">{fmtDateTime(order.delivered_at)}</p>
                </div>
              </div>
            </div>

            {/* Reported vs Received Gap (Goflow #28) */}
            {reportedGap && (
              <div className="bg-warning/5 border border-warning/40 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-warning mb-3 flex items-center gap-2">
                  <AlertTriangle size={14} /> Reported vs Recorded Gap
                </h2>
                <div className="grid grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-ink-muted">Reported</span>
                    <p className="font-mono text-ink-bright">{order.units_reported_received}</p>
                  </div>
                  <div>
                    <span className="text-ink-muted">Recorded</span>
                    <p className="font-mono text-ink-bright">{order.quantity}</p>
                  </div>
                  <div>
                    <span className="text-ink-muted">Gap</span>
                    <p className="font-mono text-warning">
                      {gapDelta != null && gapDelta > 0 ? '+' : ''}
                      {gapDelta}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Status-specific notes */}
            {showStatusNotes && (
              <div className="bg-surface-1 border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-ink-bright mb-3">{statusNotesLabel}</h2>
                {editMode ? (
                  <textarea
                    value={editStatusNotes}
                    onChange={(e) => setEditStatusNotes(e.target.value)}
                    className="arbi-input w-full text-xs resize-none min-h-[80px]"
                    placeholder={`${statusNotesLabel}…`}
                  />
                ) : (
                  <p className="text-xs text-ink-secondary whitespace-pre-wrap">
                    {s === 'on_hold'
                      ? order.on_hold_reason
                      : s === 'followup'
                        ? order.followup_notes
                        : order.discrepancy_notes}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky top-0 space-y-4">
              <div className="bg-surface-1 border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-ink-bright mb-3">Status</h2>
                <div className="space-y-3">
                  <div>
                    <span
                      className={clsx(
                        'inline-block px-2 py-1 rounded text-xs font-medium',
                        statusClass
                      )}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  {order.previous_status && (
                    <div className="flex justify-between text-xs">
                      <span className="text-ink-muted">Previous</span>
                      <span className="text-ink-secondary">
                        {STATUS_LABELS[order.previous_status] || order.previous_status}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Channel</span>
                    <span
                      className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', channelClass)}
                    >
                      {channelLabel}
                    </span>
                  </div>
                  {order.marketplace_order_id && (
                    <div className="flex justify-between text-xs gap-2">
                      <span className="text-ink-muted">MP Order</span>
                      <span className="text-ink-secondary font-mono truncate text-2xs">
                        {order.marketplace_order_id}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-border-subtle my-1" />
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Created</span>
                    <span className="font-mono text-ink-secondary text-2xs">
                      {fmtDateTime(order.created_at)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Updated</span>
                    <span className="font-mono text-ink-secondary text-2xs">
                      {fmtDateTime(order.updated_at)}
                    </span>
                  </div>
                </div>
              </div>

              {!isDistRep && !editMode && editable && (
                <div className="bg-surface-1 border border-border rounded-lg p-4">
                  <h2 className="text-sm font-semibold text-ink-bright mb-3">Quick Actions</h2>
                  <div className="space-y-2">
                    {renderPrimaryTransition()}
                    {!['on_hold', 'discrepancy', 'followup'].includes(s) &&
                      !['delivered', 'channel_fulfilled', 'cancelled'].includes(s) && (
                        <button
                          onClick={() =>
                            void transitionStatus('on_hold', {
                              on_hold_reason: 'Placed on hold',
                            })
                          }
                          disabled={saving}
                          className="arbi-btn-ghost text-xs w-full text-warning"
                        >
                          Put On Hold
                        </button>
                      )}
                    {['new_order', 'processing', 'review', 'verified', 'fulfilling'].includes(s) &&
                      order.channel &&
                      ['amazon_fba', 'walmart_wfs'].includes(order.channel) && (
                        <button
                          onClick={() => void transitionStatus('channel_fulfilled')}
                          disabled={saving}
                          className="arbi-btn-ghost text-xs w-full"
                        >
                          Mark Channel Fulfilled
                        </button>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
